import { useEffect, useRef, useState } from "react";
import type { FileItem } from "@titancloud/shared";

interface PreviewFilmstripProps {
  files: FileItem[];
  index: number;
  onSelect: (index: number) => void;
}

const TILE_SIZE = 52;
const TILE_GAP = 6;
const STEP = TILE_SIZE + TILE_GAP;
// How many thumbnails to keep mounted on either side of the current one. Each
// tile is positioned by its own absolute index (never relative to a moving
// window), so tiles mounting/unmounting at the edges never disturbs the ones
// that stay — only the row's single transform animates, which is what keeps
// the slide smooth regardless of how far the user jumps.
const WINDOW_RADIUS = 30;

/**
 * Bottom filmstrip, like a phone gallery's swipe strip: a fixed-size row of
 * thumbnails that glides to keep the current file centered, and lets the user
 * jump straight to any nearby file. Not user-scrollable — it only moves in
 * response to navigation.
 */
export function PreviewFilmstrip({ files, index, onSelect }: PreviewFilmstripProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (files.length < 2) return null;

  const windowStart = Math.max(0, index - WINDOW_RADIUS);
  const windowEnd = Math.min(files.length, index + WINDOW_RADIUS + 1);
  const rowOffset = containerWidth / 2 - TILE_SIZE / 2 - index * STEP;

  return (
    <div
      ref={containerRef}
      className="relative h-16 w-full flex-shrink-0 overflow-hidden border-t border-white/10 bg-black/40"
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-black to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-black to-transparent" />

      <div
        className="absolute inset-y-0 left-0 transition-transform duration-300 ease-out"
        style={{ transform: `translateX(${rowOffset}px)` }}
      >
        {files.slice(windowStart, windowEnd).map((file, i) => {
          const absoluteIndex = windowStart + i;
          const isActive = absoluteIndex === index;
          return (
            <button
              key={file.id}
              type="button"
              onClick={() => onSelect(absoluteIndex)}
              title={file.name}
              className={`absolute top-1/2 -translate-y-1/2 overflow-hidden rounded-md transition-all duration-200 ${
                isActive ? "z-10 scale-110 ring-2 ring-white" : "opacity-60 hover:opacity-90"
              }`}
              style={{ left: absoluteIndex * STEP, width: TILE_SIZE, height: TILE_SIZE }}
            >
              {file.thumbnailUrl ? (
                <img
                  src={file.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-800 text-gray-500">
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              {file.mimeType.startsWith("video/") && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/20">
                  <svg className="h-4 w-4 text-white drop-shadow" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M4.5 3.5A1 1 0 003 4.5v11a1 1 0 001.5.866l9-5.5a1 1 0 000-1.732l-9-5.5a1 1 0 00-1-.134z" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
